// ─── Portal M06 — Axios clients separados del ERP ────────────

import axios from 'axios';
import type { ApiResponse, PaginatedResponse } from '@/types';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const PORTAL_CLIENTE_TOKEN_KEY = 'portal_cliente_token';
const PORTAL_BROKER_TOKEN_KEY  = 'portal_broker_token';

// ─── Portal Cliente — Axios instance ─────────────────────────

const portalClienteApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

portalClienteApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(PORTAL_CLIENTE_TOKEN_KEY);
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Portal Broker — Axios instance ──────────────────────────

const portalBrokerApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

portalBrokerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(PORTAL_BROKER_TOKEN_KEY);
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Tipos compartidos ────────────────────────────────────────

export interface EstadoCuentaContrato {
  contrato_id:    number;
  folio:          string;
  proyecto_nombre: string;
  unidad_clave:   string;
  monto_total:    string;
  proximo_vencimiento: string | null;
  monto_proximo:  string | null;
  mora_acumulada: string;
  saldo_pendiente: string;
}

export interface EstadoCuenta {
  cliente_id:   number;
  nombre:       string;
  contratos:    EstadoCuentaContrato[];
}

export interface ProyectoPublico {
  id:               number;
  nombre:           string;
  enganche_min_pct: number;
  enganche_max_pct: number;
  meses_opciones:   number[];
  tasa_interes_anual: number;
  usa_tasa_interes: boolean;
  precio_base:      number;
}

// ── Cotizador Broker Types ────────────────────────────────────────────────────

export interface PagoIntermedio {
  nombre: string;
  pct: number;
  monto: number;
}

export interface UnidadDisponible {
  id: number;
  nombre: string;
  clave: string;
  tipo: string;
  precio_lista: number;
  metros_cuadrados?: number | null;
  nivel?: number | null;
  orientacion?: string | null;
}

export interface CotizadorConfig {
  enganche_min_pct: number;
  enganche_max_pct: number;
  meses_opciones: number[];
  usa_tasa_interes: boolean;
  tasa_interes_anual: number;
  estructura_pagos: Array<{ nombre: string; pct: number; mes_estimado?: number }>;
  descuento_max_pct?: number | null;
}

export interface CotizadorEmisor {
  razon_social: string;
  rfc: string;
  domicilio: string | null;
}

export interface CotizadorDeposito {
  banco: string;
  titular: string;
  cuenta: string;
  clabe: string;
}

export interface CotizadorAsesor {
  nombre: string;
  email: string;
  telefono: string;
}

export interface CotizadorConfigResponse {
  proyecto: { id: number; nombre: string; slug: string };
  tiene_mapa: boolean;
  cotizador_config: CotizadorConfig;
  unidades_disponibles: UnidadDisponible[];
  emisor?: CotizadorEmisor | null;
  deposito?: CotizadorDeposito | null;
  /**
   * 🔇 Qué decirle al broker cuando la cotización va a salir SIN datos de depósito.
   * Nace de I-C1: `deposito` en `null` se pintaba como cuatro campos vacíos y **nadie
   * decía nada** — el broker le entregaba al comprador un papel sin a dónde pagar y se
   * enteraba el día que el cliente llamaba a preguntar.
   */
  deposito_aviso?: string | null;
  asesor?: CotizadorAsesor | null;
}

export interface LeadBrokerPayload {
  tipo: 'cliente' | 'broker';
  cliente_nombre: string;
  cliente_email: string;
  cliente_celular: string;
  cliente_nacionalidad: string;
  broker_empresa?: string;
  broker_nombre?: string;
  unidad_tipo: string;
  unidad_id?: number;          // unidad específica elegida (trazabilidad + aviso de disponibilidad)
  precio_lista: number;        // precio ORIGINAL de lista (sin descuento)
  descuento_pct?: number;
  enganche_pct: number;
  meses: number;
  tasa_interes_anual?: number;
  pago_mensual: number;
  saldo_financiar: number;
  total_intereses?: number;
  pagos_intermedios: PagoIntermedio[];
}

export interface LeadBrokerResponse {
  prospecto_id: number;
  folio: string;
  excede_descuento_max?: boolean;
  descuento_max_pct?: number | null;
  unidad_no_disponible?: boolean;
}

// ─── Portal Cliente — endpoints ───────────────────────────────

export const portalClienteValidarToken = (token: string) =>
  portalClienteApi
    .post<ApiResponse<{ cliente_id: number; nombre: string; access_token: string }>>(
      '/portal/cliente/auth',
      { token },
    )
    .then((r) => r.data.data);

export const portalClienteGetEstadoCuenta = () =>
  portalClienteApi
    .get<ApiResponse<EstadoCuenta>>('/portal/cliente/estado-cuenta')
    .then((r) => r.data.data);

// El backend (getCuotasContratoPortal → verPlanPago) ya manda `tipo_pago` en cada fila
// (revisión adversarial 26-ago, hallazgo #4/ESPEJO) — el tipo lo declaraba, la pantalla
// lo pintaba, y sin él una liquidación de $1,272,000 se veía igual que una mensualidad.
export const portalClienteGetCuotas = (contratoId: number) =>
  portalClienteApi
    .get<ApiResponse<Array<{ id: number; numero_pago: number; tipo_pago: string; fecha_vencimiento: string; monto: string; estado: string }>>>(
      `/portal/cliente/contratos/${contratoId}/cuotas`,
    )
    .then((r) => r.data.data);

export const portalClienteSubirComprobante = (
  contratoId: number,
  payload: { monto_reportado: number; fecha_reportada: string },
  archivo: File,
) => {
  const form = new FormData();
  form.append('monto_reportado', String(payload.monto_reportado));
  form.append('fecha_reportada', payload.fecha_reportada);
  form.append('archivo', archivo);
  return portalClienteApi
    .post<ApiResponse<{ id: number; folio?: string; estado: string }>>(
      `/portal/cliente/contratos/${contratoId}/comprobantes`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    .then((r) => r.data.data);
};

export const portalClienteSubirDocumentoKYC = (
  prospectoId: number,
  tipoDocumento: string,
  archivo: File,
) => {
  const form = new FormData();
  form.append('tipo_documento', tipoDocumento);
  form.append('archivo', archivo);
  return portalClienteApi
    .post<ApiResponse<{ id: number; tipo_documento: string; documento_estado: string }>>(
      `/portal/cliente/prospectos/${prospectoId}/documentos`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    .then((r) => r.data.data);
};

export const portalClienteGetDocumentos = (prospectoId: number) =>
  portalClienteApi
    .get<ApiResponse<Array<{ id: number; tipo_documento: string; documento_estado: string; nombre_archivo: string }>>>(
      `/portal/cliente/prospectos/${prospectoId}/documentos`,
    )
    .then((r) => r.data.data);

// ─── Portal Broker — endpoints ────────────────────────────────

export const portalBrokerValidarToken = (token: string) =>
  portalBrokerApi
    .post<ApiResponse<{ broker_id: number; nombre: string; access_token: string }>>(
      '/portal/broker/auth',
      { token },
    )
    .then((r) => r.data.data);

export async function portalBrokerLogin(params: {
  email: string;
  password: string;
  tenant: string;
}): Promise<{ token: string; broker_id: number; nombre: string }> {
  // Use bare axios — no interceptor should inject any stored token on a login call
  const res = await axios.post<ApiResponse<{ token: string; broker_id: number; nombre: string }>>(
    `${BASE_URL}/portal/broker/login`,
    params,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.data.data;
}

export const portalBrokerGetProyectos = () =>
  portalBrokerApi
    .get<{ data: Array<{ id: number; nombre: string; estado: string }> }>(
      '/portal/broker/proyectos',
    )
    .then((r) => r.data.data ?? []);

export interface RegistrarProspectoPayload {
  proyecto_id: number;
  nombre:      string;
  apellido:    string;
  email:       string;
  telefono?:   string;
  nota?:       string;
}

export const portalBrokerRegistrarProspecto = (payload: RegistrarProspectoPayload) =>
  portalBrokerApi
    .post<ApiResponse<{ id: number; folio: string }>>(
      '/portal/broker/prospectos',
      payload,
    )
    .then((r) => r.data.data);

// ── Cotizador Broker API calls ────────────────────────────────────────────────

export async function portalBrokerGetCotizadorConfig(
  proyectoId: number
): Promise<CotizadorConfigResponse> {
  const res = await portalBrokerApi.get<ApiResponse<CotizadorConfigResponse>>(`/broker/cotizador/${proyectoId}`);
  return res.data.data;
}

export async function portalBrokerGetMapa(proyectoId: number): Promise<any> {
  const res = await portalBrokerApi.get<ApiResponse<any>>(`/broker/proyectos/${proyectoId}/mapa`);
  return res.data.data;
}

export async function portalBrokerRegistrarLead(
  proyectoId: number,
  payload: LeadBrokerPayload
): Promise<LeadBrokerResponse> {
  const res = await portalBrokerApi.post<ApiResponse<LeadBrokerResponse>>(`/broker/cotizador/${proyectoId}/lead`, payload);
  return res.data.data;
}

// ─── Simulador Público — sin auth ─────────────────────────────

const publicoApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

export const fetchSimuladorProyecto = (proyectoId: number) =>
  publicoApi
    .get<ApiResponse<ProyectoPublico>>(`/publico/simulador/${proyectoId}`)
    .then((r) => r.data.data);

export { PORTAL_CLIENTE_TOKEN_KEY, PORTAL_BROKER_TOKEN_KEY };
